import { GlobalOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  ConfigProvider,
  Divider,
  Space,
  type ThemeConfig,
  Tooltip,
  Typography,
} from 'antd';
import React from 'react';

import { useDocumentTitle } from '../hooks/use-document-title';
import { BORDER_RADIUS, FONT_SIZE, SPACING } from '../theme/tokens';
import CopyToClipboard from './copy-to-clipboard';

const { Title } = Typography;

const HEADER_CONTROL_HEIGHT = 32;
const HEADER_TAG_BORDER_WIDTH = 1;

export const DETAIL_PAGE_HEADER_THEME: ThemeConfig = {
  components: {
    Tag: {
      fontSizeSM: FONT_SIZE.md,
      lineHeightSM:
        (HEADER_CONTROL_HEIGHT - 2 * HEADER_TAG_BORDER_WIDTH) / FONT_SIZE.md,
      borderRadiusSM: BORDER_RADIUS,
    },
  },
};

interface DetailPageHeaderProps {
  title: string;
  onReload?: () => void;
  isReloadDisabled?: boolean;
  isReloadHighlighted?: boolean;
  actions?: React.ReactNode;
  tags?: React.ReactNode;
  info?: React.ReactNode;
  uuid?: string;
  links?: React.ReactNode;
  externalUrl?: string;
}

function DetailPageHeader({
  title,
  onReload,
  isReloadDisabled = false,
  isReloadHighlighted = false,
  actions,
  tags,
  info,
  uuid,
  links,
  externalUrl,
}: DetailPageHeaderProps): React.ReactElement {
  useDocumentTitle(title);

  return (
    <>
      <Title level={2}>{title}</Title>
      <ConfigProvider theme={DETAIL_PAGE_HEADER_THEME}>
        <Space
          size={SPACING.lg}
          align="center"
          wrap
          style={{ marginBottom: SPACING.md }}
        >
          {onReload && (
            <Tooltip title="Reload">
              <Button
                type={isReloadHighlighted ? 'primary' : 'default'}
                icon={<ReloadOutlined />}
                aria-label="Reload"
                disabled={isReloadDisabled}
                onClick={(): void => {
                  onReload();
                }}
              />
            </Tooltip>
          )}
          {actions}
          {tags && (
            <Space size={SPACING.sm} align="center" wrap>
              {tags}
            </Space>
          )}
          {info}
          {uuid && <CopyToClipboard text={uuid} size="middle" />}
          {links}
          {externalUrl && (
            <Tooltip title="Open on website">
              <Button
                href={externalUrl}
                target="_blank"
                rel="noreferrer noopener nofollow"
                aria-label={`Open ${title} on website`}
                icon={<GlobalOutlined />}
              />
            </Tooltip>
          )}
        </Space>
      </ConfigProvider>
      <Divider />
    </>
  );
}

export default DetailPageHeader;
